import React, { useEffect, useMemo, useState } from "react";
import { api, errorMsg, getJson, uploadFile } from "../api";
import PcbUploadFields from "./PcbUploadFields";

function boolFromBackend(value) {
  if (value === true || value === false) return value;
  return false;
}

function fileNameFromPayload(payload, field) {
  const directName = String(payload?.[`${field}_name`] || "").trim();
  if (directName) return directName;

  const url = payload?.[field];
  if (!url) return "";

  try {
    const clean = String(url).split("?")[0];
    return decodeURIComponent(clean.split("/").pop() || "");
  } catch (_) {
    return String(url);
  }
}

function filePresent(payload, field) {
  return !!(payload?.[`${field}_present`] || payload?.[field] || payload?.[`${field}_name`]);
}

export default function StepGerber({ projectId, onSuccess, uploadedName, isRequired = false }) {
  const [format, setFormat] = useState("gerber");
  const [archiveFile, setArchiveFile] = useState(null);
  const [booting, setBooting] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");

  const [asm, setAsm] = useState({ smd_side: "" });
  const [existingFiles, setExistingFiles] = useState({});
  const [includeInArchive, setIncludeInArchive] = useState({
    pick_place_in_archive: false,
    assembly_plan_in_archive: false,
    paste_top_in_archive: false,
    paste_bot_in_archive: false,
  });
  const [supportFiles, setSupportFiles] = useState({
    pick_place_file: null,
    assembly_plan_file: null,
    placement_file: null,
    paste_top: null,
    paste_bot: null,
    notes: "",
  });

  const requiredMark = isRequired ? " *" : "";

  useEffect(() => {
    let mounted = true;

    async function boot() {
      setBooting(true);
      try {
        const [cfg, filesRes] = await Promise.all([
          getJson(`/user/projects/${projectId}/pcb-config/`).catch(() => ({})),
          getJson(`/user/projects/${projectId}/customer-pcb-files/`).catch(() => ({})),
        ]);

        if (!mounted) return;

        const asmSpec = cfg?.assembly_spec || {};
        setAsm({ smd_side: asmSpec?.smd_side || "" });

        const saved = filesRes?.customer_pcb_files || cfg?.customer_pcb_files || {};
        setExistingFiles(saved);
        setIncludeInArchive({
          pick_place_in_archive: boolFromBackend(saved.pick_place_in_archive),
          assembly_plan_in_archive: boolFromBackend(saved.assembly_plan_in_archive),
          paste_top_in_archive: boolFromBackend(saved.paste_top_in_archive),
          paste_bot_in_archive: boolFromBackend(saved.paste_bot_in_archive),
        });
        setSupportFiles((prev) => ({ ...prev, notes: saved.notes || "" }));

        const hasOdb = filePresent(saved, "odb_archive");
        const hasGerber = filePresent(saved, "gerber_zip");
        setFormat(hasOdb && !hasGerber ? "odb" : "gerber");
      } catch (e) {
        if (mounted) {
          setError(errorMsg(e, "Impossible de charger les fichiers PCB actuels."));
        }
      } finally {
        if (mounted) setBooting(false);
      }
    }

    boot();
    return () => {
      mounted = false;
    };
  }, [projectId]);

  function existingName(field) {
    return fileNameFromPayload(existingFiles, field);
  }

  function setSupportFile(field, file) {
    setSupportFiles((prev) => ({ ...prev, [field]: file }));
    if (file) {
      setExistingFiles((prev) => ({
        ...prev,
        [field]: null,
        [`${field}_name`]: "",
        [`${field}_present`]: false,
      }));
    }
  }

  function clearSupportFile(field) {
    setSupportFiles((prev) => ({ ...prev, [field]: null }));
    setExistingFiles((prev) => ({
      ...prev,
      [field]: null,
      [`${field}_name`]: "",
      [`${field}_present`]: false,
    }));
  }

  function handleFormatChange(nextFormat) {
    setFormat(nextFormat);
    setArchiveFile(null);
    setError("");
    setOk("");
  }

  function confirmArchiveFlag(field, nextValue) {
    if (!nextValue) {
      setIncludeInArchive((prev) => ({ ...prev, [field]: false }));
      return;
    }

    const okConfirm = window.confirm("Êtes-vous sûr que ce fichier est bien inclus dans votre archive Gerber ?");
    if (!okConfirm) return;
    setIncludeInArchive((prev) => ({ ...prev, [field]: true }));
  }

  const effectivePresence = useMemo(() => {
    const existingGerber = filePresent(existingFiles, "gerber_zip");
    const existingOdb = filePresent(existingFiles, "odb_archive");
    const existingPickPlace = filePresent(existingFiles, "pick_place_file");
    const existingPlacement = filePresent(existingFiles, "placement_file");
    const existingAssemblyPlan = filePresent(existingFiles, "assembly_plan_file");
    const existingPasteTop = filePresent(existingFiles, "paste_top");
    const existingPasteBot = filePresent(existingFiles, "paste_bot");
    const includeAllSupportFiles = format === "odb";

    const hasGerberArchive = format === "gerber"
      ? !!(archiveFile || existingGerber)
      : !!existingGerber;
    const hasOdbArchive = format === "odb"
      ? !!(archiveFile || existingOdb)
      : !!existingOdb;

    return {
      hasArchive: hasGerberArchive || hasOdbArchive,
      hasGerberArchive,
      hasOdbArchive,
      hasPickPlace: !!(
        includeAllSupportFiles ||
        supportFiles.pick_place_file ||
        supportFiles.placement_file ||
        existingPickPlace ||
        existingPlacement ||
        includeInArchive.pick_place_in_archive
      ),
      hasAssemblyPlan: !!(
        includeAllSupportFiles ||
        supportFiles.assembly_plan_file ||
        supportFiles.placement_file ||
        existingAssemblyPlan ||
        existingPlacement ||
        includeInArchive.assembly_plan_in_archive
      ),
      hasPasteTop: !!(
        includeAllSupportFiles ||
        supportFiles.paste_top ||
        existingPasteTop ||
        includeInArchive.paste_top_in_archive
      ),
      hasPasteBot: !!(
        includeAllSupportFiles ||
        supportFiles.paste_bot ||
        existingPasteBot ||
        includeInArchive.paste_bot_in_archive
      ),
    };
  }, [archiveFile, existingFiles, format, includeInArchive, supportFiles]);

  function validateBeforeSubmit() {
    if (format === "gerber") {
      if (!effectivePresence.hasGerberArchive) {
        throw new Error("Veuillez fournir un fichier Gerber ZIP.");
      }
      if (archiveFile && !archiveFile.name.toLowerCase().endsWith(".zip")) {
        throw new Error("Le fichier Gerber doit être un .zip");
      }
    } else {
      if (!effectivePresence.hasOdbArchive) {
        throw new Error("Veuillez fournir une archive ODB++.");
      }
      if (
        archiveFile &&
        !archiveFile.name.toLowerCase().endsWith(".zip") &&
        !archiveFile.name.toLowerCase().endsWith(".tgz") &&
        !archiveFile.name.toLowerCase().endsWith(".tar.gz")
      ) {
        throw new Error("Le fichier ODB++ doit être un .zip ou .tgz");
      }
      return;
    }

    if (!effectivePresence.hasPickPlace) {
      throw new Error("Veuillez fournir le fichier pick and place ou confirmer qu'il est déjà dans l'archive.");
    }
    if (!effectivePresence.hasAssemblyPlan) {
      throw new Error("Veuillez fournir le plan d'implantation ou confirmer qu'il est déjà dans l'archive.");
    }
    if (asm.smd_side === "top" && !effectivePresence.hasPasteTop) {
      throw new Error("Veuillez fournir PASTE TOP ou confirmer qu'il est déjà dans l'archive.");
    }
    if (asm.smd_side === "bottom" && !effectivePresence.hasPasteBot) {
      throw new Error("Veuillez fournir PASTE BOT ou confirmer qu'il est déjà dans l'archive.");
    }
    if (asm.smd_side === "both" && (!effectivePresence.hasPasteTop || !effectivePresence.hasPasteBot)) {
      throw new Error("Veuillez fournir PASTE TOP et PASTE BOT, ou confirmer leur présence dans l'archive.");
    }
  }

  async function uploadSupportFiles() {
    const fd = new FormData();

    if (format === "odb" && archiveFile) fd.append("odb_archive", archiveFile);

    if (format !== "odb") {
      if (supportFiles.pick_place_file) fd.append("pick_place_file", supportFiles.pick_place_file);
      if (supportFiles.assembly_plan_file) fd.append("assembly_plan_file", supportFiles.assembly_plan_file);
      if (supportFiles.placement_file) fd.append("placement_file", supportFiles.placement_file);
      if (supportFiles.paste_top) fd.append("paste_top", supportFiles.paste_top);
      if (supportFiles.paste_bot) fd.append("paste_bot", supportFiles.paste_bot);
    }

    fd.append("pick_place_in_archive", String(format === "odb" ? true : includeInArchive.pick_place_in_archive));
    fd.append("assembly_plan_in_archive", String(format === "odb" ? true : includeInArchive.assembly_plan_in_archive));
    fd.append("paste_top_in_archive", String(format === "odb" ? true : includeInArchive.paste_top_in_archive));
    fd.append("paste_bot_in_archive", String(format === "odb" ? true : includeInArchive.paste_bot_in_archive));
    fd.append("notes", String(supportFiles.notes || "").trim());

    const res = await api.put(`/user/projects/${projectId}/customer-pcb-files/`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    const payload = res?.data?.customer_pcb_files || {};
    setExistingFiles(payload);
    return res?.data;
  }

  const submit = async () => {
    setError("");
    setOk("");
    setLoading(true);

    try {
      validateBeforeSubmit();

      let mainUploadPayload = null;
      if (format === "gerber" && archiveFile) {
        mainUploadPayload = await uploadFile(`/user/projects/${projectId}/gerber-upload/`, archiveFile);
      }

      const supportPayload = await uploadSupportFiles();
      const archiveName =
        archiveFile?.name ||
        (format === "gerber" ? existingName("gerber_zip") : existingName("odb_archive")) ||
        uploadedName ||
        "";

      setArchiveFile(null);
      setSupportFiles((prev) => ({
        ...prev,
        pick_place_file: null,
        assembly_plan_file: null,
        placement_file: null,
        paste_top: null,
        paste_bot: null,
      }));

      setOk(
        format === "gerber"
          ? "Fichiers Gerber et production téléversés avec succès."
          : "Archive ODB++ téléversée avec succès."
      );

      onSuccess?.(mainUploadPayload || supportPayload || {}, archiveName);
    } catch (e) {
      setError(errorMsg(e, "Erreur téléversement"));
    } finally {
      setLoading(false);
    }
  };

  const disabledAll = loading || booting;

  return (
    <div className="stepper-section">
      <h2>4) Téléverser fichiers PCB</h2>

      <PcbUploadFields
        format={format}
        disabled={disabledAll}
        onFormatChange={handleFormatChange}
        formatInputName="pcb-format"
        requiredMark={requiredMark}
        archiveFile={archiveFile}
        archiveExistingName={format === "gerber" ? existingName("gerber_zip") : existingName("odb_archive")}
        onArchivePick={setArchiveFile}
        onArchiveClear={() => {
          setArchiveFile(null);
          if (format === "gerber") {
            setExistingFiles((prev) => ({ ...prev, gerber_zip: null, gerber_zip_name: "", gerber_zip_present: false }));
          } else {
            setExistingFiles((prev) => ({ ...prev, odb_archive: null, odb_archive_name: "", odb_archive_present: false }));
          }
        }}
        supportFiles={supportFiles}
        getExistingName={existingName}
        onSupportFilePick={setSupportFile}
        onSupportFileClear={clearSupportFile}
        includeInArchive={includeInArchive}
        onArchiveToggle={confirmArchiveFlag}
        notes={supportFiles.notes}
        onNotesChange={(notes) => setSupportFiles((prev) => ({ ...prev, notes }))}
      />

      {!archiveFile && uploadedName && !existingName(format === "gerber" ? "gerber_zip" : "odb_archive") ? (
        <p className="muted" style={{ marginTop: 8 }}>
          Dernier fichier téléversé : <b>{uploadedName}</b>
        </p>
      ) : null}

      {error ? <div className="alert error" style={{ marginTop: 10 }}>{error}</div> : null}
      {ok ? <div className="alert ok" style={{ marginTop: 10 }}>{ok}</div> : null}

      <button
        className="btn primary"
        onClick={submit}
        disabled={disabledAll}
        style={{ marginTop: 14 }}
      >
        {loading
          ? "Téléversement en cours..."
          : format === "gerber"
            ? "Téléverser Gerber + fichiers de production"
            : "Téléverser archive ODB++"}
      </button>
    </div>
  );
}
